import { useState } from "react";

import { Colors, Icon } from "@blueprintjs/core";
import { useMediaQuery } from "react-responsive";
import { useSearchParams } from "react-router";

import { Box, Flex } from "@/components/layout/flexbox";
import { isXsQuery, Media } from "@/components/layout/Media";
import { Container } from "@/components/pieces/bootstrap";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { FiveStars } from "@/components/pieces/marketing/FiveStars";
import { MarketingVideo } from "@/components/pieces/marketing/MarketingVideo";
import { ProButton, ProLink } from "@/components/pieces/Pro";
import { MarketingPageConfig } from "@/types/marketing";

interface MainHeroProps {
  options: MarketingPageConfig;
  onCtaClick?: () => void;
}

export function MainHero({ options, onCtaClick }: MainHeroProps) {
  const [searchParams] = useSearchParams();
  const video = searchParams.get("video");
  const [showingVideo, setShowingVideo] = useState(!!video);
  const isXs = useMediaQuery(isXsQuery);
  const defaultFontSize = "60px";
  const defaultXsFontSize = "40px";
  let fontSize =
    options && options.headlineFontSize
      ? options.headlineFontSize
      : defaultFontSize;
  if (isXs) {
    fontSize = defaultXsFontSize;
  }

  const headline =
    options && options.headline ? (
      <div dangerouslySetInnerHTML={{ __html: options.headline }} />
    ) : (
      <>{options.defaultHeadline}</>
    );

  const subHeader =
    options && options.subHeader ? (
      <div dangerouslySetInnerHTML={{ __html: options.subHeader }} />
    ) : (
      <>{options.defaultSubHeader}</>
    );

  const hasStarRating = options && options.hasStarRating;

  const flexPositioning = ["center", "center", "flex-start", "flex-start"];

  const heroContent = (
    <Flex
      justifyContent={["center", "center", "space-between"]}
      alignItems={["center", "center", "stretch"]}
      flexWrap={["wrap", "wrap", "nowrap"]}
      minHeight={650}
    >
      <Flex
        py={40}
        textAlign={["center", "center", "left"]}
        flex={[undefined, undefined, 1]}
        flexDirection="column"
        justifyContent="center"
      >
        <h1
          style={{
            fontSize,
            fontWeight: 700,
            color: "#FFF",
          }}
          className="leading-tight"
        >
          {headline}
        </h1>
        <Flex justifyContent={flexPositioning} color="#FFF">
          <Box width={["100%", "100%", 560]} fontSize={22}>
            {subHeader}
          </Box>
        </Flex>
        <Flex mt={40} flexWrap="wrap" justifyContent={flexPositioning}>
          {onCtaClick ? (
            <ProButton style={{ width: "350px" }} onClick={onCtaClick}>
              <Flex py={2} fontSize={22} width={260} justifyContent="center">
                {options.ctaButtonText || "Get Started"}
              </Flex>
            </ProButton>
          ) : (
            <ProLink
              width="350px"
              to={
                options.ctaLink ||
                `/match/${
                  options.specialtySlug
                }?type=find%20my%20agent%20button&source=${encodeURIComponent(
                  "pro page",
                )}`
              }
            >
              <Flex py={2} fontSize={22} width={260} justifyContent="center">
                {options.ctaButtonText || "Find My Coach"}
              </Flex>
            </ProLink>
          )}
        </Flex>
        {hasStarRating && (
          <Flex
            mt={3}
            alignItems="center"
            justifyContent={["center", "center", "flex-start"]}
          >
            <FiveStars />
            <Box color={Colors.WHITE}>4.9 Average Session Rating</Box>
          </Flex>
        )}
      </Flex>
      <Flex
        justifyContent={["space-around", "space-around", "flex-end"]}
        flex={[undefined, undefined, 1]}
        alignItems="flex-end"
      >
        <Box className="relative">
          <img
            src={options.heroGraphic}
            style={{ width: options.heroGraphicWidth, maxWidth: "100%" }}
          />
          <Clickable onClick={() => setShowingVideo(true)}>
            <Flex
              sx={{
                position: "absolute",
                left: "calc(50% - 90px)",
                top: "calc(50% + 90px)",
                borderRadius: 10,
                backgroundColor: options.playDark
                  ? "rgba(0,0,0,0.5)"
                  : "rgba(255,255,255,0.2)",
                ":hover": {
                  backgroundColor: options.playDark
                    ? "rgba(0,0,0,0.65)"
                    : "rgba(255,255,255,0.3)",
                },
              }}
              p={3}
              alignItems="center"
              fontWeight={600}
              color="#FFF"
            >
              <Icon icon="play" size={32} color="#FFF" />
              <Box ml={1}>WATCH VIDEO</Box>
            </Flex>
          </Clickable>
        </Box>
      </Flex>
    </Flex>
  );

  return (
    <Box css={{ background: options.background }}>
      <div className="hero main">
        <Media lessThan="sm">{heroContent}</Media>
        <Media greaterThanOrEqual="sm">
          <Container className="!max-w-marketing-content">
            {heroContent}
          </Container>
        </Media>
        {options.marketingVideoId ? (
          <MarketingVideo
            videoId={options.marketingVideoId}
            visible={showingVideo}
            setVisible={setShowingVideo}
          />
        ) : null}
      </div>
    </Box>
  );
}
